/**
 * KE BREAKDOWN CARD
 * 
 * Componente de auditoria para transparência total do CAPM
 * Exibe breakdown detalhado do Cost of Equity (Ke) e WACC
 * 
 * Features:
 * - Tabela com todos os componentes do Ke
 * - Toggle para metodologia CRP (multiplicative/additive)
 * - Warnings automáticos de validação CAPM
 * - Expandable para Specific Risk detalhado
 * - Fórmulas em LaTeX para documentação
 * 
 * @module components/audit/KeBreakdownCard
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Chip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { IconDashboard } from '../../utils/icons';
import { touchFriendlyIconButton } from '../../utils/mobileFriendlyStyles';
import type {
  KeBreakdown,
  WACCBreakdown,
  CAPMComponents,
  CRPMethodology,
} from '../../types/valuation';
import { formatPercent } from '../../utils/capmCalculator';

// ============================================================================
// INTERFACES
// ============================================================================

export interface KeBreakdownCardProps {
  /** Nome da empresa */
  empresa: 'Sétima' | '555 Studios';
  
  /** Componentes CAPM calculados */
  capmComponents: CAPMComponents;
  
  /** Breakdown detalhado do Ke */
  keBreakdown: KeBreakdown;
  
  /** Breakdown do WACC (opcional) */
  waccBreakdown?: WACCBreakdown;
  
  /** Warnings de validação */
  warnings?: string[];
  
  /** Erros críticos */
  errors?: string[];
  
  /** Permitir mudança de metodologia CRP */
  allowCRPToggle?: boolean;
  
  /** Callback ao mudar metodologia CRP */
  onCRPMethodologyChange?: (methodology: CRPMethodology) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function KeBreakdownCard({
  empresa,
  capmComponents,
  keBreakdown,
  waccBreakdown,
  warnings = [],
  errors = [],
  allowCRPToggle = false,
  onCRPMethodologyChange,
}: KeBreakdownCardProps) {
  const theme = useTheme();
  const [expandedSpecificRisk, setExpandedSpecificRisk] = useState(false);
  const [expandedWACC, setExpandedWACC] = useState(false);

  const hasWarnings = warnings.length > 0;
  const hasErrors = errors.length > 0;

  // Handler para toggle CRP
  const handleCRPMethodologyChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMethodology: CRPMethodology | null
  ) => {
    if (newMethodology && onCRPMethodologyChange) {
      onCRPMethodologyChange(newMethodology);
    }
  };

  return (
    <Card elevation={3} sx={{ mb: 3 }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconDashboard size={24} />
              Breakdown CAPM - {empresa}
            </Typography>
            {hasErrors && (
              <Chip
                icon={<WarningIcon />}
                label="ERRO"
                color="error"
                size="small"
              />
            )}
            {!hasErrors && hasWarnings && (
              <Chip
                icon={<WarningIcon />}
                label="ATENÇÃO"
                color="warning"
                size="small"
              />
            )}
            {!hasErrors && !hasWarnings && (
              <Chip
                icon={<CheckCircleIcon />}
                label="OK"
                color="success"
                size="small"
              />
            )}
          </Box>
        }
        subheader="Transparência total do Cost of Equity (Ke)"
      />

      <CardContent>
        {/* CRP Methodology Toggle */}
        {allowCRPToggle && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Metodologia CRP:
            </Typography>
            <ToggleButtonGroup
              value={capmComponents.crpMethodology}
              exclusive
              onChange={handleCRPMethodologyChange}
              size="small"
            >
              <ToggleButton value="additive">
                <Tooltip title="CRP separado: Ke = Rf + β×ERP + CRP + Size + Specific (Damodaran)">
                  <span>Additive</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="multiplicative">
                <Tooltip title="CRP embutido: Ke = Rf + β×(ERP+CRP) + Size + Specific">
                  <span>Multiplicative</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Validation Alerts */}
        {hasErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Erros Críticos</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {hasWarnings && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Warnings de Validação</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Ke Breakdown Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Componente</strong></TableCell>
                <TableCell align="right"><strong>Valor</strong></TableCell>
                <TableCell><strong>Descrição</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Risk-free Rate */}
              <TableRow>
                <TableCell>Rf</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontFamily="monospace">
                    {formatPercent(keBreakdown.rf.valor)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {keBreakdown.rf.label}
                  </Typography>
                  <Chip
                    label={keBreakdown.rf.fonte}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </TableCell>
              </TableRow>

              {/* Beta × Market Risk */}
              <TableRow>
                <TableCell>β × Market Risk</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontFamily="monospace">
                    {formatPercent(keBreakdown.beta_x_market_risk.valor)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {keBreakdown.beta_x_market_risk.label}
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={`β = ${keBreakdown.beta_x_market_risk.beta.toFixed(2)}`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5 }}
                    />
                    <Chip
                      label={`ERP = ${formatPercent(keBreakdown.beta_x_market_risk.erp)}`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5 }}
                    />
                    {keBreakdown.beta_x_market_risk.crp !== undefined && (
                      <Chip
                        label={`CRP = ${formatPercent(keBreakdown.beta_x_market_risk.crp)}`}
                        size="small"
                        variant="outlined"
                        color={capmComponents.crpMethodology === 'multiplicative' ? 'warning' : 'default'}
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>

              {/* CRP Additive (se aplicável) */}
              {keBreakdown.crp_additive && (
                <TableRow>
                  <TableCell>CRP (additive)</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontFamily="monospace">
                      {formatPercent(keBreakdown.crp_additive.valor)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {keBreakdown.crp_additive.label}
                    </Typography>
                    <Chip
                      label="Damodaran"
                      size="small"
                      variant="outlined"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  </TableCell>
                </TableRow>
              )}

              {/* Size Premium */}
              <TableRow>
                <TableCell>Size Premium</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontFamily="monospace">
                    {formatPercent(keBreakdown.sizePremium.valor)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {keBreakdown.sizePremium.label}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Specific Risk (expandable) */}
              <TableRow>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    Specific Risk
                    {keBreakdown.specificRisk.breakdown && (
                      <IconButton
                        onClick={() => setExpandedSpecificRisk(!expandedSpecificRisk)}
                        sx={{
                          ...touchFriendlyIconButton,
                          ml: 1,
                          transform: expandedSpecificRisk ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontFamily="monospace">
                    {formatPercent(keBreakdown.specificRisk.valor)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {keBreakdown.specificRisk.label}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Specific Risk Breakdown (collapsible) */}
              {keBreakdown.specificRisk.breakdown && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ p: 0 }}>
                    <Collapse in={expandedSpecificRisk} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Breakdown do Specific Risk:
                        </Typography>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Size Risk</TableCell>
                              <TableCell align="right">
                                {formatPercent(keBreakdown.specificRisk.breakdown.size)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Liquidity Risk</TableCell>
                              <TableCell align="right">
                                {formatPercent(keBreakdown.specificRisk.breakdown.liquidity)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Key Person Risk</TableCell>
                              <TableCell align="right">
                                {formatPercent(keBreakdown.specificRisk.breakdown.keyPerson)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Execution Risk</TableCell>
                              <TableCell align="right">
                                {formatPercent(keBreakdown.specificRisk.breakdown.execution)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}

              {/* Total Ke */}
              <TableRow 
                sx={{ 
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(100, 181, 246, 0.08)'
                    : 'rgba(33, 150, 243, 0.08)',
                }}
              >
                <TableCell>
                  <strong>Cost of Equity (Ke)</strong>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body1" 
                    fontFamily="monospace" 
                    fontWeight="bold"
                    sx={{
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(100, 181, 246, 1)'
                        : 'rgba(33, 150, 243, 1)',
                    }}
                  >
                    {formatPercent(keBreakdown.total.valor)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {keBreakdown.total.label}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* WACC Breakdown (opcional) */}
        {waccBreakdown && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h6" component="span">
                WACC Breakdown
              </Typography>
              <IconButton
                onClick={() => setExpandedWACC(!expandedWACC)}
                sx={{
                  ...touchFriendlyIconButton,
                  ml: 1,
                  transform: expandedWACC ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>

            <Collapse in={expandedWACC} timeout="auto">
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Componente</strong></TableCell>
                      <TableCell align="right"><strong>Taxa</strong></TableCell>
                      <TableCell align="right"><strong>Peso</strong></TableCell>
                      <TableCell align="right"><strong>Contribuição</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Equity */}
                    <TableRow>
                      <TableCell>Cost of Equity (Ke)</TableCell>
                      <TableCell align="right">
                        {formatPercent(waccBreakdown.ke.valor)}
                      </TableCell>
                      <TableCell align="right">
                        {formatPercent(waccBreakdown.ke.peso)}
                      </TableCell>
                      <TableCell align="right">
                        {formatPercent(waccBreakdown.ke.contribuicao)}
                      </TableCell>
                    </TableRow>

                    {/* Debt (se houver) */}
                    {waccBreakdown.kd && (
                      <TableRow>
                        <TableCell>
                          Cost of Debt (Kd)
                          <Typography variant="caption" display="block" color="text.secondary">
                            After-tax: {formatPercent(waccBreakdown.kd.valorAfterTax)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatPercent(waccBreakdown.kd.valor)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercent(waccBreakdown.kd.peso)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercent(waccBreakdown.kd.contribuicao)}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* WACC Total */}
                    <TableRow sx={{ bgcolor: 'success.light' }}>
                      <TableCell>
                        <strong>WACC</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {waccBreakdown.wacc.formula}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2} />
                      <TableCell align="right">
                        <Typography variant="body1" fontFamily="monospace" fontWeight="bold">
                          {formatPercent(waccBreakdown.wacc.valor)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Capital Structure Info */}
              <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Estrutura de Capital:</strong><br />
                  • Equity: {formatPercent(waccBreakdown.capitalStructure.equityWeight)} do capital total<br />
                  • Debt: {formatPercent(waccBreakdown.capitalStructure.debtWeight)} do capital total<br />
                  • D/E Ratio: {waccBreakdown.capitalStructure.debtToEquity.toFixed(2)}
                </Typography>
              </Alert>
            </Collapse>
          </>
        )}

        {/* Fórmula Reference */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Fórmula CAPM:</strong><br />
            {capmComponents.crpMethodology === 'additive' ? (
              <>Ke = Rf + β×ERP + CRP + Size Premium + Specific Risk</>
            ) : (
              <>Ke = Rf + β×(ERP + CRP) + Size Premium + Specific Risk</>
            )}
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
}
